import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { friendlyError } from "@/lib/errors";
import { useActiveOrg } from "@/features/tenancy/hooks";
import type { Membership } from "@/features/tenancy/api";
import { Button } from "@/components/ui/Button";
import { Panel } from "@/components/ui/Panel";
import { Field } from "@/components/ui/Form";
import { toast } from "@/components/ui/toast";
import { Skeleton } from "@/components/ui/states";
import { createProduct, fetchTaxonomy } from "./productApi";

/**
 * Enlace legible a partir del nombre. Se quitan las tildes antes de filtrar,
 * o «Diseño Café» acabaria en «dise-o-caf-» con dos huecos donde habia letras.
 */
function slugify(texto: string): string {
  return texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** SKU sugerido: iniciales legibles, no un identificador opaco. */
function sugerirSku(nombre: string): string {
  const base = slugify(nombre).replace(/-/g, "").slice(0, 8).toUpperCase();
  return base ? `${base}-01` : "";
}

const ETIQUETA: Record<string, string> = {
  physical: "Producto",
  service: "Servicio",
};

/**
 * El formulario no se monta hasta saber que vende la tienda.
 *
 * Antes esto era un solo componente y `kind` salia de
 * `useState(tiposPermitidos[0])`. `useState` lee su valor inicial UNA vez, en
 * el primer render, y en ese momento `useMemberships` todavia esta cargando:
 * `org` es null y se usaba el valor de reserva. Cuando la organizacion llegaba
 * con {service}, `kind` ya se habia quedado en 'physical' para siempre.
 *
 * En una tienda de productos acertaba por casualidad. En una de solo servicios
 * mandaba el tipo prohibido y lo rebotaba products_kind_allowed al guardar,
 * despues de que alguien hubiera llenado el formulario entero.
 *
 * Separarlo en dos evita toda esa clase de error: el formulario nace con los
 * datos reales, sin efectos que sincronicen estado a posteriori.
 */
export function NewProductPage() {
  const { org, isPending } = useActiveOrg();

  if (isPending || !org) return <FormSkeleton />;

  return <NewProductForm org={org} />;
}

function NewProductForm({ org }: { org: Membership }) {
  const navigate = useNavigate();
  const qc = useQueryClient();

  // Lo que la tienda tiene permitido vender (0040). Si solo admite un tipo,
  // no se pregunta: se usa ese y no se dibuja el control.
  const tiposPermitidos = org.catalogKinds;
  const puedeElegirTipo = tiposPermitidos.length > 1;

  const [nombre, setNombre] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTocado, setSlugTocado] = useState(false);
  const [kind, setKind] = useState(tiposPermitidos[0] ?? "physical");
  const [categoryId, setCategoryId] = useState("");
  const [sku, setSku] = useState("");
  const [skuTocado, setSkuTocado] = useState(false);
  const [precio, setPrecio] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [intentado, setIntentado] = useState(false);

  const taxonomy = useQuery({
    queryKey: ["taxonomy", org.organizationId],
    queryFn: () => fetchTaxonomy(org.organizationId),
  });

  // El slug y el SKU se derivan del nombre mientras nadie los toque a mano.
  // En cuanto se editan dejan de seguirlo: sobrescribir lo que alguien acaba
  // de escribir es la clase de ayuda que estorba.
  function cambiarNombre(valor: string) {
    setNombre(valor);
    if (!slugTocado) setSlug(slugify(valor));
    if (!skuTocado) setSku(sugerirSku(valor));
  }

  const esServicio = kind === "service";

  /**
   * El SKU es una idea de tienda: identifica una unidad en un estante. Una
   * clase de manejo o una consultoria no tienen. Pero la columna es NOT NULL,
   * asi que hay que poner algo — y se pone solo, sin preguntarlo.
   *
   * Se deriva del slug porque el slug YA es unico por organizacion
   * (products_organization_id_slug_key), asi que el SKU hereda esa unicidad
   * sin necesidad de comprobar nada ni de reintentar ante un choque.
   */
  const skuFinal = esServicio ? slug.trim().toUpperCase() : sku.trim();

  const precioValido = /^\d+(\.\d{1,2})?$/.test(precio.trim()) && Number(precio) > 0;
  const errores = {
    nombre: !nombre.trim() ? "Ponle un nombre." : undefined,
    slug: !slug.trim() ? "Hace falta un enlace." : undefined,
    sku: !esServicio && !sku.trim() ? "Hace falta un SKU." : undefined,
    precio: !precioValido ? "Un precio mayor que cero, con hasta dos decimales." : undefined,
  };
  const valido = !errores.nombre && !errores.slug && !errores.sku && !errores.precio;

  async function guardar() {
    setIntentado(true);
    if (!valido) return;

    setGuardando(true);
    try {
      const id = await createProduct({
        organizationId: org.organizationId,
        name: nombre.trim(),
        slug: slug.trim(),
        kind,
        categoryId: categoryId || null,
        sku: skuFinal,
        price: precio.trim(),
      });

      await qc.invalidateQueries({ queryKey: ["products"] });
      toast.ok("Producto creado. Está oculto hasta que lo publiques.");
      navigate(`/catalogo/${id}`);
    } catch (e) {
      toast.error(friendlyError(e));
      setGuardando(false);
    }
  }

  return (
    <div className="flex flex-col gap-3 pb-16">
      <div className="flex items-start gap-3">
        <Link
          to="/catalogo"
          className="mt-0.5 grid size-7 place-items-center rounded-md text-ink-2 hover:bg-sunk"
          aria-label="Volver al catálogo"
        >
          <ArrowLeft className="size-4" />
        </Link>
        <div>
          <h1 className="text-head font-semibold tracking-tight">Nuevo producto</h1>
          <p className="text-xs text-ink-3">
            {puedeElegirTipo
              ? "Se crea oculto y con una variante, para que puedas terminarlo antes de publicarlo."
              : `Tu tienda vende ${ETIQUETA[tiposPermitidos[0]!]?.toLowerCase() ?? "productos"}s. Se crea oculto y con una variante.`}
          </p>
        </div>
      </div>

      <div className="grid gap-3 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <Panel title="Información">
          <div className="flex flex-col gap-3.5">
            <Field label="Nombre" required error={intentado ? errores.nombre : undefined}>
              <input
                autoFocus
                value={nombre}
                onChange={(e) => cambiarNombre(e.target.value)}
                className="w-full rounded-md border border-line bg-surface px-3 py-2 text-ui"
              />
            </Field>

            {puedeElegirTipo && (
              <Field
                label="Tipo"
                hint={
                  kind === "service"
                    ? "No lleva inventario: nunca aparece agotado y su pedido no caduca por falta de pago."
                    : "Lleva inventario: al venderse reserva stock y puede agotarse."
                }
              >
                <select
                  value={kind}
                  onChange={(e) => setKind(e.target.value)}
                  className="w-full rounded-md border border-line bg-surface px-3 py-2 text-ui"
                >
                  {tiposPermitidos.includes("physical") && (
                    <option value="physical">Producto</option>
                  )}
                  {tiposPermitidos.includes("service") && (
                    <option value="service">Servicio</option>
                  )}
                </select>
              </Field>
            )}

            <Field
              label="Enlace en la tienda"
              required
              hint={slug ? `/${slug}` : "Se arma solo desde el nombre."}
              error={intentado ? errores.slug : undefined}
            >
              <input
                value={slug}
                onChange={(e) => {
                  setSlugTocado(true);
                  setSlug(slugify(e.target.value));
                }}
                className="tnum w-full rounded-md border border-line bg-surface px-3 py-2 text-ui"
              />
            </Field>

            <Field label="Categoría" hint="Opcional. Se puede cambiar después.">
              <select
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                className="w-full rounded-md border border-line bg-surface px-3 py-2 text-ui"
              >
                <option value="">Sin categoría</option>
                {(taxonomy.data?.categories ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>
        </Panel>

        <Panel title="Primera variante">
          <div className="flex flex-col gap-3.5">
            {/* Toda venta pasa por una variante: `create_order` cobra el
                precio de la variante, no del producto. Un producto sin
                ninguna aparece en el catalogo y no se puede comprar, asi que
                la primera se crea aqui y no se deja para luego. */}
            {!esServicio && (
              <Field label="SKU" required error={intentado ? errores.sku : undefined}>
                <input
                  value={sku}
                  onChange={(e) => {
                    setSkuTocado(true);
                    setSku(e.target.value.toUpperCase());
                  }}
                  className="tnum w-full rounded-md border border-line bg-surface px-3 py-2 text-ui"
                />
              </Field>
            )}

            <Field
              label={`Precio (${org.currency})`}
              required
              hint="Solo números, con punto decimal."
              error={intentado ? errores.precio : undefined}
            >
              <input
                inputMode="decimal"
                value={precio}
                onChange={(e) => setPrecio(e.target.value.replace(",", "."))}
                className="tnum w-full rounded-md border border-line bg-surface px-3 py-2 text-ui"
              />
            </Field>

            <p className="text-xs text-ink-3">
              Se crea una variante llamada «Única». Si el {ETIQUETA[kind]?.toLowerCase() ?? "producto"}{" "}
              tiene tramos, tallas o duraciones, se añaden desde el detalle.
            </p>
          </div>
        </Panel>
      </div>

      <div className="flex items-center gap-2">
        <Button onClick={() => void guardar()} disabled={guardando}>
          {guardando ? "Creando…" : "Crear producto"}
        </Button>
        <Button variant="secondary" onClick={() => navigate("/catalogo")} disabled={guardando}>
          Cancelar
        </Button>
      </div>
    </div>
  );
}

function FormSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      <Skeleton className="h-6 w-44" />
      <div className="grid gap-3 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <Skeleton className="h-80 rounded-lg" />
        <Skeleton className="h-80 rounded-lg" />
      </div>
    </div>
  );
}
